
import { SalesTransaction, AllocationBasis } from "./types";

/**
 * Interface for allocation rule definitions
 */
export interface AllocationRule {
  scenario: string;
  basis: AllocationBasis;
  apply: (transactions: SalesTransaction[], params: any) => SalesTransaction[];
}

/**
 * Multi-geo account allocation rule
 * Splits revenue across multiple geographies based on specified basis
 */
export const multiGeoAllocationRule: AllocationRule = {
  scenario: 'Multi-geo accounts',
  basis: AllocationBasis.SHIP_TO_VOLUME,
  apply: (transactions: SalesTransaction[], params: { 
    accountId: string, 
    geoSplits: { country: string, percentage: number }[] 
  }) => {
    const { accountId, geoSplits } = params;
    const accountTransactions = transactions.filter(t => t.accountId === accountId);
    const otherTransactions = transactions.filter(t => t.accountId !== accountId);
    
    // Validate that percentages sum to 100%
    const totalPercentage = geoSplits.reduce((sum, split) => sum + split.percentage, 0);
    if (Math.abs(totalPercentage - 100) > 0.01) {
      console.error('Allocation percentages must sum to 100%');
      return transactions;
    }
    
    // Create allocated transactions based on geo splits
    const allocatedTransactions = accountTransactions.flatMap(transaction => {
      return geoSplits.map(split => {
        const allocatedRevenue = transaction.revenue * (split.percentage / 100);
        return {
          ...transaction,
          shipToCountry: split.country,
          revenue: allocatedRevenue,
          attributionType: "Allocated" as const,
          allocationMethod: `Split by ${AllocationBasis.SHIP_TO_VOLUME} (${split.percentage}%)`
        };
      });
    });
    
    return [...otherTransactions, ...allocatedTransactions];
  }
};

/**
 * Indirect/channel sales allocation rule
 */
export const channelSalesAllocationRule: AllocationRule = {
  scenario: 'Indirect/channel sales',
  basis: AllocationBasis.SELL_THROUGH,
  apply: (transactions: SalesTransaction[], params: { 
    channelAccountId: string, 
    endUserSplits: { country: string, percentage: number }[] 
  }) => {
    const { channelAccountId, endUserSplits } = params;
    const channelTransactions = transactions.filter(t => t.accountId === channelAccountId);
    const otherTransactions = transactions.filter(t => t.accountId !== channelAccountId);
    
    // Create allocated transactions based on end-user splits
    const allocatedTransactions = channelTransactions.flatMap(transaction => {
      return endUserSplits.map(split => {
        const allocatedRevenue = transaction.revenue * (split.percentage / 100);
        return {
          ...transaction,
          shipToCountry: split.country,
          revenue: allocatedRevenue,
          attributionType: "Channel" as const,
          allocationMethod: `Channel allocation by ${AllocationBasis.SELL_THROUGH} (${split.percentage}%)`
        };
      });
    });
    
    return [...otherTransactions, ...allocatedTransactions];
  }
};

/**
 * Bundle/multi-product deal allocation rule
 */
export const bundleAllocationRule: AllocationRule = {
  scenario: 'Bundles/multi-product deals',
  basis: AllocationBasis.LIST_PRICE,
  apply: (transactions: SalesTransaction[], params: { 
    bundleInvoiceId: string, 
    productSplits: { productId: string, percentage: number }[] 
  }) => {
    const { bundleInvoiceId, productSplits } = params;
    const bundleTransactions = transactions.filter(t => t.invoiceId === bundleInvoiceId);
    const otherTransactions = transactions.filter(t => t.invoiceId !== bundleInvoiceId);
    
    // Create allocated transactions based on product splits
    const allocatedTransactions = bundleTransactions.flatMap(transaction => {
      return productSplits.map(split => {
        const allocatedRevenue = transaction.revenue * (split.percentage / 100);
        return {
          ...transaction,
          productId: split.productId,
          revenue: allocatedRevenue,
          attributionType: "Allocated" as const,
          allocationMethod: `Bundle allocation by ${AllocationBasis.LIST_PRICE} (${split.percentage}%)`
        };
      });
    });
    
    return [...otherTransactions, ...allocatedTransactions];
  }
};

export const allocationRules: AllocationRule[] = [
  multiGeoAllocationRule,
  channelSalesAllocationRule,
  bundleAllocationRule
];
